import React, { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'localization';
import type { A2uiComponentEntry, SpaceDocumentLimits, SpaceValidationError } from 'types/space';
import { expandComposition, type CompositionResolver } from '@center-platform/space-format/expand';
import type { ComponentEntry } from '@center-platform/component-catalog';
import { getAdapter } from './adapterRegistry';
import './CompositionHost.scss';
import { resolveBoundValue } from './resolveBoundValue';
import { surfaceReducer, INITIAL_SURFACE_STATE, type SurfaceState } from './surfaceReducer';
import { useSpaceBusOrNull, type WireHandler } from './spaceBus';
import type {
  AdapterContext,
  CompositionEnvironment,
  CompositionHostMode,
  SelectedNode,
  SpaceAction,
  SpaceHostMessage,
} from './types';

export interface CompositionHostHandle {
  /** Resolves a rendered node's id to its registry entry — the composer UI's inspect hook (SC10). */
  inspect: (componentId: string) => SelectedNode | null;
}

export interface CompositionHostProps {
  /** v1.0 message-loop messages applied in order (declaratively re-derived each render). */
  messages: SpaceHostMessage[];
  surfaceId: string;
  mode?: CompositionHostMode;
  env?: CompositionEnvironment;
  /** Looks up a composition's stored definition by ref; absent = no composition registry attached. */
  resolveComposition?: CompositionResolver;
  limits?: Partial<SpaceDocumentLimits>;
  /** Catalog entries available for inspect resolution (code-backed + composition-backed). */
  catalogEntries?: ComponentEntry[];
  onAction?: (action: SpaceAction) => void;
  onSelect?: (node: SelectedNode | null) => void;
  /**
   * Resolves and selects this component id the first time it becomes resolvable after mount
   * (EAINT-865 gap 14) — the deep-link consuming side for `?node=` links from finding rows. Fires
   * exactly once per host lifetime (a later document edit that changes `expanded` does not
   * re-fire it), the same as any other one-shot mount effect; a caller wanting to select a
   * different node later should call `onSelect` directly rather than remount the host.
   */
  initialSelectedComponentId?: string;
  /**
   * Called whenever composition expansion records errors (cycle, post-expansion size, a
   * definition whose declared root matches none of its components). Expansion is fail-closed
   * and returns the partially-built tree, so without this the host would render a silently
   * truncated Space with no signal anywhere.
   */
  onExpansionErrors?: (errors: SpaceValidationError[]) => void;
  className?: string;
}

const UNKNOWN_COMPONENT_CLASS = 'composition-host__unknown';

function findCatalogEntry(type: string, catalogEntries: ComponentEntry[] | undefined): ComponentEntry | undefined {
  return catalogEntries?.find((entry) => entry.type === type);
}

interface ExpandedTree {
  components: A2uiComponentEntry[];
  root: string;
  errors: SpaceValidationError[];
}

function resolveNode(
  componentId: string,
  expanded: ExpandedTree | null,
  catalogEntries: ComponentEntry[] | undefined,
  resolveComposition: CompositionResolver | undefined
): SelectedNode | null {
  const entry = expanded?.components.find((c) => c.id === componentId);
  if (!entry) return null;
  const catalogEntry = findCatalogEntry(entry.component, catalogEntries);
  const compositionRef = catalogEntry
    ? undefined
    : { systemName: entry.component, version: entry.componentVersion as number | undefined };
  const contextViewBinding = entry.contextViewBinding as SelectedNode['contextViewBinding'];
  const gridPosition = entry.gridPosition as SelectedNode['gridPosition'];
  return {
    componentId,
    componentType: entry.component,
    catalogEntry,
    compositionRef,
    contextViewBinding,
    compositionInterface: compositionRef ? resolveComposition?.(compositionRef)?.interface : undefined,
    gridPosition,
  };
}

const CompositionHost = forwardRef<CompositionHostHandle, CompositionHostProps>(
  (
    {
      messages,
      surfaceId,
      mode = 'live',
      env = {},
      resolveComposition,
      limits,
      catalogEntries,
      onAction,
      onSelect,
      initialSelectedComponentId,
      onExpansionErrors,
      className,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const bus = useSpaceBusOrNull();
    const consumedInitialSelectionRef = React.useRef(false);

    // One stable subscribe closure per component id, kept for the life of the bus. `renderNode`
    // runs on every host render, so an inline `(handler) => bus.subscribe(id, handler)` would hand
    // each adapter a new function identity every time and make any effect keyed on it unsubscribe
    // and resubscribe on every render — churn proportional to the number of wired nodes.
    const wireSubscribers = useMemo(() => {
      const perId = new Map<string, (handler: WireHandler) => () => void>();
      return {
        for: (id: string): ((handler: WireHandler) => () => void) | undefined => {
          if (!bus) return undefined;
          const existing = perId.get(id);
          if (existing) return existing;
          const subscribe = (handler: WireHandler) => bus.subscribe(id, handler);
          perId.set(id, subscribe);
          return subscribe;
        },
        prune: (keep: Map<string, A2uiComponentEntry>) => {
          // No `perId.size <= keep.size` shortcut: replacing a 10-node document with a DIFFERENT
          // 10-node one is the common edit, and that guard would skip it (10 ≤ 10), letting the
          // map double before the next render prunes. Same size ≠ same ids.
          perId.forEach((_subscribe, id) => {
            if (!keep.has(id)) perId.delete(id);
          });
        },
      };
    }, [bus]);
    const onWireDeliveryFor = wireSubscribers.for;

    // Purely derived from `messages` — buffer-until-root falls out of `mounted` staying false until
    // a `createSurface`/`updateComponents` message resolves `root` to a known component (SC1).
    const state: SurfaceState = useMemo(
      () => messages.reduce(surfaceReducer, INITIAL_SURFACE_STATE),
      [messages]
    );

    const expanded: ExpandedTree | null = useMemo(() => {
      if (!state.mounted || !state.root) return null;
      const allComponents = Object.values(state.components);
      if (!resolveComposition) {
        return { components: allComponents, root: state.root, errors: [] };
      }
      const result = expandComposition({ components: allComponents, root: state.root }, resolveComposition, limits);
      return { components: result.components, root: result.root, errors: result.errors };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.mounted, state.root, state.components, resolveComposition, limits]);

    const expansionErrors = expanded?.errors;
    useEffect(() => {
      if (expansionErrors?.length) onExpansionErrors?.(expansionErrors);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expansionErrors]);

    useImperativeHandle(
      ref,
      () => ({
        inspect: (componentId: string) => resolveNode(componentId, expanded, catalogEntries, resolveComposition),
      }),
      [expanded, catalogEntries, resolveComposition]
    );

    useEffect(() => {
      if (!initialSelectedComponentId || consumedInitialSelectionRef.current || !expanded) return;
      // Consume the deep-link only once it actually RESOLVES. An id that isn't in the document
      // yet (it may only appear after a later expansion) must not burn the one shot — and must
      // never clear a selection the user already made by dispatching `onSelect(null)`.
      const node = resolveNode(initialSelectedComponentId, expanded, catalogEntries, resolveComposition);
      if (!node) return;
      consumedInitialSelectionRef.current = true;
      onSelect?.(node);
    }, [initialSelectedComponentId, expanded, catalogEntries, resolveComposition, onSelect]);

    if (!state.mounted || !state.root || !expanded) {
      return null;
    }

    const byId = new Map(expanded.components.map((entry) => [entry.id, entry]));

    // Prune closures for ids the document no longer contains. The map is populated during render
    // (`renderNode` asks for a subscriber as it walks) and is only rebuilt when `bus` changes —
    // i.e. essentially never for a mounted Space. Without this, StrictMode's discarded first render
    // and every `updateComponents` edit leave a closure per historical id for the life of the host,
    // so the map grows with the composer's edit history rather than with the document.
    wireSubscribers.prune(byId);

    const handleAction = (action: SpaceAction) => {
      onAction?.(action);
    };

    const handleSelect = (componentId: string) => {
      onSelect?.(resolveNode(componentId, expanded, catalogEntries, resolveComposition));
    };

    const selectFromEventTarget = (target: EventTarget | null) => {
      const node = (target as HTMLElement | null)?.closest('[data-component-id]');
      const componentId = node?.getAttribute('data-component-id');
      if (componentId) handleSelect(componentId);
    };

    /**
     * `scopedDataModel` is the data model this subtree resolves `$bind` pointers against. It is
     * the surface data model everywhere except inside a repeater (`List`), which renders one
     * instance of its template per item and needs each instance to see its OWN item — hence
     * `renderChildInScope`, which overlays the repeater's per-item keys for that subtree only.
     * `keySuffix` keeps React keys unique when the same template id is rendered N times.
     */
    function renderNode(
      id: string,
      scopedDataModel: Record<string, unknown> = state.dataModel,
      keySuffix = ''
    ): React.ReactNode {
      const entry = byId.get(id);
      if (!entry) return null;
      const reactKey = keySuffix ? `${id}${keySuffix}` : id;

      const ctx: AdapterContext = {
        renderChild: (childId: string) => renderNode(childId, scopedDataModel, keySuffix),
        renderChildren: (ids: string[]) => ids.map((childId) => renderNode(childId, scopedDataModel, keySuffix)),
        renderChildInScope: (childId: string, scope: Record<string, unknown>, childKeySuffix: string) =>
          renderNode(childId, { ...scopedDataModel, ...scope }, `${keySuffix}${childKeySuffix}`),
        getComponentEntry: (id: string) => byId.get(id),
        resolveValue: (bound: unknown) => resolveBoundValue(bound, scopedDataModel),
        onAction: handleAction,
        surfaceId,
        mode,
        env,
        dataModel: scopedDataModel,
        onWireDelivery: onWireDeliveryFor(id),
      };

      const AdapterComponent = getAdapter(entry.component);
      const { id: _id, component: _component, ...restProps } = entry;

      if (!AdapterComponent) {
        return (
          <div key={reactKey} data-component-id={id} className={UNKNOWN_COMPONENT_CLASS}>
            {t('space.render.unknownComponent', { component: entry.component })}
          </div>
        );
      }

      return <AdapterComponent key={reactKey} componentId={id} props={restProps} ctx={ctx} />;
    }

    return (
      <div
        className={['composition-host', className].filter(Boolean).join(' ')}
        data-composition-host-surface={surfaceId}
        data-composition-host-mode={mode}
        onClickCapture={(event) => selectFromEventTarget(event.target)}
        onKeyDownCapture={(event) => {
          // Composer selection must be reachable without a pointer. The wrapper is not
          // itself interactive (it delegates to whichever descendant carries
          // data-component-id), so the keyboard path mirrors the capture-phase click.
          if (event.key !== 'Enter' && event.key !== ' ') return;
          selectFromEventTarget(event.target);
        }}
      >
        {renderNode(expanded.root)}
      </div>
    );
  }
);

CompositionHost.displayName = 'CompositionHost';

export default CompositionHost;
